import { Decimal, Money } from "classes/DecimalClasses";

import { getItemRecord } from "services/sosInventoryService/domainLogic";
import { calculateAdjustmentCostBasis } from "services/sosInventoryService/sosApi";
import { afterTouchLine as transferAfterTouchLine } from "services/sosInventoryService/transfer/afterTouchLine";
import { setOutboundBinsForLine, formatBinInfo } from "services/utility/bins";
import {
  getBaseUom,
  getUomConversionFromUomReference,
} from "services/utility/uoms";

export async function fetchCostBasis(line, date) {
  if (!line.item?.id) {
    return new Money(0);
  }

  const conversion = getUomConversionFromUomReference(line.uom, line.item.uoms);
  const { costBasis } = await calculateAdjustmentCostBasis(
    line.item.id,
    line.quantityDiff.times(conversion),
    date
  );
  return costBasis;
}

export async function updateTransferLineWithItem(item, line, record) {
  const { fromLocation } = record;
  if (!item) {
    return line;
  }
  // transfer the appropriate properties from the new inventory
  // item to the line item
  let newLine = {
    ...line,
    item,
    description: item.description,
    uom: getBaseUom(item.uoms),
    onhand: item.onhand,
    class: item.class,
    serials: [],
    relatedRecords: { item },
    itemDetails: {
      itemUoms: item.uoms,
      serialTracking: item.serialTracking,
      lotTracking: item.lotTracking,
      type: item.type,
    },
  };

  newLine = await setOutboundBinsForLine(fromLocation, newLine, "fromBin");
  return newLine;
}

export async function updateAdjustmentLineWithItem(item, line) {
  if (!item) {
    return line;
  }
  // transfer the appropriate properties from the new inventory
  // item to the line item
  const newLine = {
    ...line,
    item,
    description: item.description,
    uom: getBaseUom(item.uoms),
    available: item.available,
    onhand: item.onhand,
    class: item.class,
    newQuantity: item.onhand,
    quantityDiff: Decimal.ZERO,
    valueDiff: Decimal.ZERO,
    relatedRecords: { item },
    serials: [],
    itemDetails: {
      itemUoms: item.uoms,
      serialTracking: item.serialTracking,
      lotTracking: item.lotTracking,
      type: item.type,
    },
  };

  // if we have locationBins, update bin options
  if (item.locationBins) {
    const filteredAndFormattedBinOptions = formatBinInfo(item.locationBins);
    newLine.availableBins = filteredAndFormattedBinOptions;
    // use item.bin (already the default bin) directly rather than re-fetching the item
    newLine.bin = item.bin?.id
      ? filteredAndFormattedBinOptions.find(({ id }) => id === item.bin.id) ??
        null
      : null;
  } else {
    newLine.availableBins = null;
    newLine.bin = null;
  }

  return newLine;
}

export async function updateTransferLineRelatedRecordsItem(
  fromLocation,
  date,
  lines,
  itemFieldsNeeded
) {
  return await Promise.all(
    lines.map(async (line) => {
      if (line.item?.id) {
        const item = await getItemRecord(
          line.item.id,
          fromLocation?.id,
          date,
          itemFieldsNeeded
        );
        const uom = item.uoms.find(({ uom }) => uom.id === line.uom?.id);
        const newLine = {
          ...line,
          uom: uom ? { ...uom, id: line.uom.id } : null,
          onhand: item.onhand,
          relatedRecords: { item },
        };
        return transferAfterTouchLine(newLine, "uom");
      }
      return line;
    })
  );
}
